import {
    clearUserValueFromStorage,
    loadUserValueFromStorage,
    saveUserValueToStorage,
} from 'custom_hooks/useStorage';
import { getPartialObject, getPropertyNames } from 'utils/object';
import { isPartialObjectValid, ObjectConstraints } from 'utils/object-validation';

export type StorageConfig<TState extends Record<string, unknown>> = Readonly<{
    baseKey: string;
    constraints: ObjectConstraints<TState>;
    defaults: TState;
}>;

export type StorageUtils<
    TState extends Record<string, unknown>,
    TScope extends string = string,
> = Readonly<{
    getStorageKey: (scope: TScope) => string;
    load: (userEmail: string, scope: TScope) => TState | undefined;
    save: (userEmail: string, scope: TScope, state: TState) => void;
    clear: (userEmail: string, scope: TScope) => void;
}>;

function createParser<TState extends Record<string, unknown>>(
    constraints: ObjectConstraints<TState>,
    defaults: TState,
): (json: unknown) => TState | null {
    const isValidState = (state: unknown): state is Partial<TState> =>
        isPartialObjectValid(state, constraints);

    return (json: unknown): TState | null => {
        if (typeof json !== 'string') {
            return null;
        }
        try {
            const parsedState = JSON.parse(json);
            return isValidState(parsedState) ? { ...defaults, ...parsedState } : null;
        } catch {
            return null;
        }
    };
}

/**
 * Creates storage utilities for persisted state.
 * Tries sessionStorage first, falls back to localStorage.
 * Saves to BOTH for cross-tab persistence.
 */
export function createStorageUtils<
    TState extends Record<string, unknown>,
    TScope extends string = string,
>(config: StorageConfig<TState>): StorageUtils<TState, TScope> {
    const { baseKey, constraints, defaults } = config;
    const parser = createParser(constraints, defaults);
    const propertyNames = getPropertyNames(constraints);

    function getStorageKey(scope: TScope): string {
        return `${scope}.${baseKey}`;
    }

    function load(userEmail: string, scope: TScope): TState | undefined {
        const key = getStorageKey(scope);

        return (
            loadUserValueFromStorage<TState | null, unknown>(userEmail, key, null, {
                store: 'session',
                deserialize: parser,
            }) ??
            loadUserValueFromStorage<TState | null, unknown>(userEmail, key, null, {
                store: 'local',
                deserialize: parser,
            }) ??
            undefined
        );
    }

    function save(userEmail: string, scope: TScope, state: TState): void {
        const stateValues = getPartialObject(state, propertyNames);
        const json = JSON.stringify(stateValues, null);
        const key = getStorageKey(scope);

        saveUserValueToStorage(userEmail, key, json, { store: 'session' });
        saveUserValueToStorage(userEmail, key, json, { store: 'local' });
    }

    function clear(userEmail: string, scope: TScope): void {
        const key = getStorageKey(scope);

        clearUserValueFromStorage(userEmail, key, 'session');
        clearUserValueFromStorage(userEmail, key, 'local');
    }

    return {
        getStorageKey,
        load,
        save,
        clear,
    };
}

// For composite scope patterns (e.g., tab + mode)
export type CompositeStorageConfig<
    TState extends Record<string, unknown>,
    TScopeArgs extends unknown[],
> = Readonly<{
    baseKey: string;
    scopeGenerator: (...args: TScopeArgs) => string;
    constraints: ObjectConstraints<TState>;
    defaults: TState;
}>;

export type CompositeStorageUtils<
    TState extends Record<string, unknown>,
    TScopeArgs extends unknown[],
> = Readonly<{
    getStorageKey: (...args: TScopeArgs) => string;
    load: (userEmail: string, ...args: TScopeArgs) => TState | undefined;
    save: (userEmail: string, state: TState, ...args: TScopeArgs) => void;
    clear: (userEmail: string, ...args: TScopeArgs) => void;
}>;

/**
 * Creates storage utilities with a composite scope (multiple scope arguments).
 * Use when storage key depends on multiple values (e.g., tab + mode).
 */
export function createCompositeStorageUtils<
    TState extends Record<string, unknown>,
    TScopeArgs extends unknown[],
>(
    config: CompositeStorageConfig<TState, TScopeArgs>,
): CompositeStorageUtils<TState, TScopeArgs> {
    const { baseKey, scopeGenerator, constraints, defaults } = config;
    const parser = createParser(constraints, defaults);
    const propertyNames = getPropertyNames(constraints);

    function getStorageKey(...args: TScopeArgs): string {
        return `${scopeGenerator(...args)}.${baseKey}`;
    }

    function load(userEmail: string, ...args: TScopeArgs): TState | undefined {
        const key = getStorageKey(...args);

        return (
            loadUserValueFromStorage<TState | null, unknown>(userEmail, key, null, {
                store: 'session',
                deserialize: parser,
            }) ??
            loadUserValueFromStorage<TState | null, unknown>(userEmail, key, null, {
                store: 'local',
                deserialize: parser,
            }) ??
            undefined
        );
    }

    function save(userEmail: string, state: TState, ...args: TScopeArgs): void {
        const stateValues = getPartialObject(state, propertyNames);
        const json = JSON.stringify(stateValues, null);
        const key = getStorageKey(...args);

        saveUserValueToStorage(userEmail, key, json, { store: 'session' });
        saveUserValueToStorage(userEmail, key, json, { store: 'local' });
    }

    function clear(userEmail: string, ...args: TScopeArgs): void {
        const key = getStorageKey(...args);

        clearUserValueFromStorage(userEmail, key, 'session');
        clearUserValueFromStorage(userEmail, key, 'local');
    }

    return {
        getStorageKey,
        load,
        save,
        clear,
    };
}
